import { offlineQueue, type QueuedRequest, type SendOutcome } from './offline-queue'

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api`

export class ApiError extends Error {
  // DEF-81 — for a 429, the server's Retry-After in seconds (when it sent one).
  constructor(public status: number, message: string, public retryAfterSeconds?: number) {
    super(message)
  }
}

// DEF-81 — what a page shows when the API is still rate limiting after the one automatic retry.
export const RATE_LIMITED_MESSAGE = 'Too many requests right now. Please wait a few seconds and try again.'
const RATE_LIMIT_RETRY_CAP_SECONDS = 10
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// FU-POS-6c-1 — thrown when a mutating POS request can't reach the server and has
// instead been queued for automatic retry on reconnect. Distinct from ApiError
// (which means the server DID answer). Screens may treat this as "saved, pending"
// rather than a hard failure (optimistic wiring is FU-POS-6c-2).
export class RequestQueuedError extends Error {
  constructor(
    public path: string,
    public key: string,
  ) {
    super('Offline — your action was saved and will retry automatically when the connection returns.')
    this.name = 'RequestQueuedError'
  }
}

export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch {
    /* ignore */
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

// A network-layer failure (offline / DNS / connection refused) rejects the fetch
// promise, typically as a TypeError. An HTTP error status does NOT reject — it
// resolves with res.ok === false, which we surface as ApiError. Only the former
// should ever be queued.
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
}

// Auth-aware API client.
//
// getToken     — from Clerk's useAuth(); called per-request for freshness.
// restaurantId — obtained from GET /api/me after sign-in; sent as
//                x-restaurant-id on every request so the API can resolve
//                the tenant even when org_id is absent from the JWT.
// DEF-92 — the messaging WebSocket address. Same origin in the deployed app (VITE_API_URL empty);
// an absolute VITE_API_URL (local dev against a remote API) swaps http(s) for ws(s).
export function liveUrl(base: string = BASE, loc: { protocol: string; host: string } = window.location): string {
  const root = /^https?:/.test(base)
    ? base.replace(/^http/, 'ws')
    : `${loc.protocol === 'https:' ? 'wss:' : 'ws:'}//${loc.host}${base}`
  return `${root}/messaging/ws`
}

// FU-POS-8a — printing happens live or not at all (see isQueueEligible).
const isPrintPath = (path: string) => /^\/pos\/checks\/[^/]+\/print$/.test(path) || path === '/pos/drawer/open'
/** A queued payment or close replays WITHOUT its printerId: it syncs, but prints no receipt and opens no drawer later. */
export function withoutPrinter(body: string | undefined): string | undefined {
  if (!body) return body
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'printerId' in parsed) {
      const { printerId: _drop, ...rest } = parsed as Record<string, unknown>
      void _drop
      return JSON.stringify(rest)
    }
  } catch { /* not JSON — queue it as sent */ }
  return body
}

export class ApiClient {
  constructor(
    private getToken: (opts?: { template?: string }) => Promise<string | null>,
    private restaurantId?: string,
    // PA-3b: when a TOC platform-staff member is VIEWING a tenant, this is the target
    // restaurant id — sent as x-view-restaurant-id, which the API auth plugin reads to
    // scope the (read-only, audited) request. Absent for normal tenant users.
    private viewRestaurantId?: string,
  ) {}

  // DEF-92 — the in-band WebSocket auth frame: a fresh Clerk token, plus the viewed restaurant when a
  // TOC staff member is viewing one. The server honours viewRestaurantId only for a platform admin with
  // a membership there (resolveWsTenant); everyone else is resolved from the token's own org.
  async wsAuthFrame(): Promise<{ token: string; viewRestaurantId?: string } | null> {
    const token = await this.getToken()
    if (!token) return null
    return this.viewRestaurantId ? { token, viewRestaurantId: this.viewRestaurantId } : { token }
  }

  // FU-POS-6c-1 — which requests carry an Idempotency-Key AND are safe to queue
  // for replay on reconnect. Money/state mutations under /pos/* qualify; the
  // server wraps them in withIdempotency so a replay is at-most-once.
  //
  // FU-POS-6b-3: POST /pos/checks (check-open) is now INCLUDED — the server wired
  // it through withIdempotency, so a replay returns the stored 201 with the
  // checkId (the old legacy dedupe 409'd with no recovery, which is why it was
  // excluded until now). Check-open queues offline like every other POS mutation.
  //
  // Excluded:
  //   • reads (GET/HEAD) — nothing to dedupe or queue.
  //   • printing (FU-POS-8a): Print bill / Reprint and No Sale. Paper and the cash drawer are "now or never" — a bill
  //     that prints, or a drawer that pops, when the connection comes back would surprise the floor.
  //   • platform-staff views — writes are server-side 403 (read-only), so there
  //     is nothing legitimate to queue.
  private isQueueEligible(method: string, path: string): boolean {
    const m = method.toUpperCase()
    if (m !== 'POST' && m !== 'PUT' && m !== 'PATCH' && m !== 'DELETE') return false
    if (!path.startsWith('/pos/')) return false
    if (isPrintPath(path)) return false
    if (!this.restaurantId || this.viewRestaurantId) return false
    return true
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const method = (init?.method ?? 'GET').toUpperCase()
    const rid = this.restaurantId
    const eligible = this.isQueueEligible(method, path)
    const key = eligible ? newIdempotencyKey() : undefined

    try {
      try {
        return await this.send<T>(path, init, key)
      } catch (err) {
        // DEF-81 — a rate-limited READ waits the server's Retry-After (capped) and tries once more. Writes
        // are never retried here: the caller decides, so nothing can be applied twice.
        if (method === 'GET' && err instanceof ApiError && err.status === 429) {
          const wait = Math.min(Math.max(err.retryAfterSeconds ?? 5, 1), RATE_LIMIT_RETRY_CAP_SECONDS)
          await sleep(wait * 1000)
          return await this.send<T>(path, init, key)
        }
        throw err
      }
    } catch (err) {
      // Only a genuine offline/unreachable failure gets queued. A server that
      // answered (ApiError) — even with a 5xx — is a real result, not offline.
      // `rid` is always set when `eligible` (isQueueEligible checks restaurantId).
      if (eligible && key && rid && isNetworkError(err)) {
        offlineQueue.enqueue({
          key,
          method,
          path,
          body: withoutPrinter(init?.body as string | undefined),
          restaurantId: rid,
        })
        throw new RequestQueuedError(path, key)
      }
      throw err
    }
  }

  // The raw fetch + header assembly. `idempotencyKey`, when present, is sent as
  // Idempotency-Key and is STABLE across retries (minted once per logical action).
  private async send<T>(path: string, init: RequestInit | undefined, idempotencyKey?: string): Promise<T> {
    const token = await this.getToken()

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token)                  headers['Authorization']        = `Bearer ${token}`
    if (this.restaurantId)      headers['x-restaurant-id']      = this.restaurantId
    if (this.viewRestaurantId)  headers['x-view-restaurant-id'] = this.viewRestaurantId
    if (idempotencyKey)         headers['Idempotency-Key']      = idempotencyKey

    const res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    })

    if (!res.ok) {
      if (res.status === 429) {
        const header = Number(res.headers?.get?.('retry-after'))
        throw new ApiError(429, RATE_LIMITED_MESSAGE, Number.isFinite(header) && header > 0 ? header : undefined)
      }
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`)
    }

    // 204 No Content has no body to parse (recipe delete, team-lead removal): res.json() would throw on success.
    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  // FU-POS-6c-1 — replay a queued mutation. Returns:
  //   'settled' — the server answered (2xx OR a definitive 4xx/5xx). Either way
  //               the request reached the server exactly once (idempotency), so
  //               the queue drops it. A stored-outcome replay returns the original
  //               2xx; a genuine error is a real, non-retryable answer.
  //   'offline' — the fetch rejected again (still no connectivity): keep it.
  // Suitable as the OfflineQueue `Sender`.
  replay = async (entry: QueuedRequest): Promise<SendOutcome> => {
    try {
      await this.send(entry.path, { method: entry.method, body: entry.body }, entry.key)
      return 'settled'
    } catch (err) {
      if (isNetworkError(err)) return 'offline'
      return 'settled' // ApiError → the server gave a definitive answer; stop retrying
    }
  }

  /** Drain any queued mutations for this client's tenant (call on reconnect). */
  drainQueue(): Promise<void> {
    if (!this.restaurantId) return Promise.resolve()
    return offlineQueue.drain(this.restaurantId, this.replay)
  }

  get<T>(path: string)                   { return this.request<T>(path) }
  post<T>(path: string, body: unknown)   { return this.request<T>(path, { method: 'POST',   body: JSON.stringify(body) }) }
  put<T>(path: string, body: unknown)    { return this.request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }) }
  patch<T>(path: string, body?: unknown) { return this.request<T>(path, { method: 'PATCH',  body: body ? JSON.stringify(body) : undefined }) }
  delete<T>(path: string)                { return this.request<T>(path, { method: 'DELETE' }) }

  // ── Binary / multipart helpers (Module 9 D2 cert documents) ──────────────────
  // Not queued or idempotent (a file upload / binary read isn't offline-replayable).
  // Auth + tenant headers match send(); the JSON Content-Type is deliberately OMITTED
  // so the browser sets multipart/form-data + boundary for FormData, and getBlob
  // returns the raw bytes instead of parsing JSON.
  private authHeaders(token: string | null): Record<string, string> {
    const h: Record<string, string> = {}
    if (token)                 h['Authorization']        = `Bearer ${token}`
    if (this.restaurantId)     h['x-restaurant-id']      = this.restaurantId
    if (this.viewRestaurantId) h['x-view-restaurant-id'] = this.viewRestaurantId
    return h
  }

  async postForm<T>(path: string, form: FormData): Promise<T> {
    const res = await fetch(`${BASE}${path}`, { method: 'POST', body: form, headers: this.authHeaders(await this.getToken()) })
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`) }
    return res.json() as Promise<T>
  }

  async getBlob(path: string): Promise<Blob> {
    const res = await fetch(`${BASE}${path}`, { headers: this.authHeaders(await this.getToken()) })
    if (!res.ok) { const body = await res.json().catch(() => ({})); throw new ApiError(res.status, body?.error ?? `HTTP ${res.status}`) }
    return res.blob()
  }
}
